import React, { useState, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import { API_CONFIG, getApiUrl, apiFetch } from '../../config/api';
import { showApiError, getErrorMessage } from '../../utils/errorUtils';
import EditableQuoteLines from '../../components/Quotes/EditableQuoteLines';
import ConvertToProductionModal from '../../components/Quotes/ConvertToProductionModal';
import EnhancedQuoteConversion from '../Sales/EnhancedQuoteConversion';
import EntityLink from '../../components/Common/EntityLink';
import BackButton from '../../components/Common/BackButton';
import { useMessages } from '../../components/Messages';
import './Quotes.css';
import { formatAppDate, localToday } from '../../utils/dateUtils';
import { fetchAllCustomers } from '../../utils/fetchAllPages';

// Statuses chosen by hand and where each one can go (same rules as the server). CONVERTED and
// PARTIALLY_CONVERTED are set by the conversion only.
export const QUOTE_STATUS_TRANSITIONS = {
  DRAFT: ['PENDING', 'ACCEPTED', 'REJECTED', 'EXPIRED'],
  PENDING: ['DRAFT', 'ACCEPTED', 'REJECTED', 'EXPIRED'],
  ACCEPTED: ['DRAFT', 'PENDING', 'REJECTED', 'EXPIRED'],
  REJECTED: ['DRAFT', 'PENDING'],
  EXPIRED: ['DRAFT', 'PENDING']
};
const STATUS_KEYS = {
  DRAFT: 'draft',
  PENDING: 'pending',
  ACCEPTED: 'accepted',
  REJECTED: 'rejected',
  EXPIRED: 'expired',
  CONVERTED: 'converted',
  PARTIALLY_CONVERTED: 'partiallyConverted'
};

// Statuses offered in the select: the current one plus where it can go. A quotation with converted
// quantities only goes (back) to ACCEPTED; its other statuses come from the conversion.
export const quoteStatusOptions = (current, hasConversions) => {
  const targets = QUOTE_STATUS_TRANSITIONS[current] || [];
  return [current, ...targets.filter(s => !hasConversions || s === 'ACCEPTED')];
};

const ViewQuote = forwardRef(({ quoteId, onBack, editMode = false, createMode = false, initialProductId = null, onSave, onCancel, onEdit, onConvertQuote }, ref) => {
  const { t } = useTranslation('sales');
  const { t: tCommon } = useTranslation('common');
  const { showSuccess, showError, showWarning } = useMessages();
  const navigate = useNavigate();
  const [quote, setQuote] = useState(null);
  const [editData, setEditData] = useState({});
  const [loading, setLoading] = useState(false);
  // error: the quotation could not be loaded (nothing to show). saveError: a save/delete failed; the
  // record stays on screen with the message above it
  const [error, setError] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [activeTab, setActiveTab] = useState('general');
  const [hasChanges, setHasChanges] = useState(false);
  const [customers, setCustomers] = useState([]);
  // Lines are sent with the quotation (the server makes them exactly these): only send them
  // once they were loaded, so a failed load can never wipe the stored lines
  const [linesLoaded, setLinesLoaded] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [lines, setLines] = useState([]);
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [showEnhancedConversionModal, setShowEnhancedConversionModal] = useState(false);
  const [linkedPOs, setLinkedPOs] = useState(null);

  useImperativeHandle(ref, () => ({
    handleDelete,
    handleSave
  }));

  useEffect(() => {
    setSaveError(null);
    fetchCustomers();
    if (quoteId && !createMode) {
      fetchQuote();
    } else if (createMode) {
      const newQuote = {
        quotation_number: `QT-${Math.random().toString(36).substr(2, 8).toUpperCase()}`,
        customer_id: null,
        quotation_date: localToday(),
        valid_until_date: localToday(30),
        status: 'DRAFT',
        payment_terms: 'NET_30',
        delivery_terms: 'FOB',
        currency: 'USD',
        // exchange_rate removed - not in database
        // subtotal & discount_amount calculated from lines
        tax_amount: 0,
        total_amount: 0,
        notes: '',
        terms_conditions: ''
      };
      setQuote(newQuote);
      setEditData(newQuote);
      setLines([]);
      setLinesLoaded(true);
      if (initialProductId) prefillProduct(newQuote, initialProductId);
    }
  }, [quoteId, createMode, initialProductId]);

  // New quote started from another screen (product wizard): first line with that product,
  // and the product's customer when it has one
  const prefillProduct = async (newQuote, productId) => {
    try {
      const response = await apiFetch(getApiUrl(`/api/products/${productId}`));
      if (!response.ok) return;
      const product = await response.json();
      setLines([{
        temp_id: `new_${Date.now()}`,
        line_number: 1,
        product_id: product.product_id,
        product_code: product.product_code,
        product_name: product.product_name,
        description: product.description || product.product_name,
        quantity: 1,
        unit_price: product.standard_price || 0,
        unit_of_measure: product.unit_of_measure || 'PCS',
        discount_percentage: 0,
        tax_percentage: 0,
        total_price: 0
      }]);
      if (product.customer_id) {
        const withCustomer = { ...newQuote, customer_id: product.customer_id, customer_name: product.customer_name };
        setQuote(withCustomer);
        setEditData(withCustomer);
        fetchContacts(product.customer_id);
      }
    } catch (error) {
      console.error('Error prefilling product:', error);
    }
  };

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        if (editMode || createMode) {
          handleCancel();
        } else {
          // In view mode, go back to list
          if (onBack) onBack();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [editMode, createMode, onBack]);

  // Helper function to format datetime to date for HTML date inputs
  const formatDateForInput = (dateValue) => {
    if (!dateValue) return '';
    const date = new Date(dateValue);
    return date.toISOString().split('T')[0];
  };

  const fetchCustomers = async () => {
    try {
      // Only active customers can be chosen (all pages, #310); the quotation's own customer is added back below if inactive
      setCustomers(await fetchAllCustomers({ is_active: 'true' }));
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const fetchContacts = async (customerId) => {
    if (!customerId) {
      setContacts([]);
      return;
    }
    try {
      const response = await apiFetch(getApiUrl(`/api/contacts/?customer_id=${customerId}`));
      if (response.ok) {
        const data = await response.json();
        setContacts(data || []);
      } else {
        setContacts([]);
      }
    } catch (error) {
      console.error('Error fetching contacts:', error);
      setContacts([]);
    }
  };

  const fetchLines = async (targetQuoteId) => {
    try {
      const linesResponse = await apiFetch(getApiUrl(`/api/quotes/lines?quotation_id=${targetQuoteId}`));
      if (linesResponse.ok) {
        const linesData = await linesResponse.json();

        // Get unique product IDs
        const productIds = [...new Set(linesData.map(line => line.product_id))];

        // Fetch product data for each unique product ID
        const productPromises = productIds.map(async (productId) => {
          try {
            const productResponse = await apiFetch(getApiUrl(`/api/products/${productId}`));
            if (productResponse.ok) {
              return await productResponse.json();
            }
            return null;
          } catch (error) {
            console.error(`Error fetching product ${productId}:`, error);
            return null;
          }
        });

        const products = await Promise.all(productPromises);
        const productMap = {};
        products.forEach(product => {
          if (product) {
            productMap[product.product_id ?? product.id] = product;
          }
        });

        // Merge product data into lines
        const enrichedLines = linesData.map(line => ({
          ...line,
          product_name: productMap[line.product_id]?.product_name || i18n.t('sales:linkedProductionOrders.unknownProduct'),
          product_code: productMap[line.product_id]?.product_code || '',
          base_price: productMap[line.product_id]?.base_price || 0
        }));

        setLines(enrichedLines);
        setLinesLoaded(true);
      } else {
        setLines([]);
        setLinesLoaded(false);
        setError(t('quotations.view.messages.linesLoadFailed'));
      }
    } catch (error) {
      console.error('Error fetching lines:', error);
      setLines([]);
      setLinesLoaded(false);
      setError(t('quotations.view.messages.linesLoadFailed'));
    }
  };

  const fetchQuote = async () => {
    setLoading(true);
    try {
      const response = await apiFetch(getApiUrl(`/api/quotes/${quoteId}`));
      if (response.ok) {
        const data = await response.json();
        setQuote(data);
        setEditData(data);
        // Fetch quote lines
        await fetchLines(quoteId);
        // Fetch contacts if customer is set
        if (data.customer_id) {
          await fetchContacts(data.customer_id);
        }
      } else {
        setError(t('quotations.view.messages.fetchFailed'));
      }
    } catch (error) {
      console.error('Error fetching quote:', error);
      setError(t('quotations.view.messages.loadingError'));
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (field, value) => {
    setEditData(prev => ({
      ...prev,
      [field]: value
    }));
    setHasChanges(true);

    // Update customer name when customer changes; its contacts and payment terms come with it
    if (field === 'customer_id') {
      const customer = customers.find(c => c.customer_id === parseInt(value));
      if (customer) {
        setEditData(prev => ({
          ...prev,
          customer_name: customer.customer_name,
          contact_id: null,
          ...(customer.payment_terms && { payment_terms: customer.payment_terms })
        }));
        // Fetch contacts for the selected customer
        fetchContacts(parseInt(value));
      } else {
        setContacts([]);
      }
    }
  };

  // Lines in the API format; null when one is incomplete (the user is told which one)
  const buildLinesPayload = () => {
    const payload = [];
    for (const [i, line] of lines.entries()) {
      const quantity = parseFloat(line.quantity);
      const unitPrice = parseFloat(line.unit_price || 0);
      let problem = null;
      if (!line.product_id) problem = 'lineProductRequired';
      else if (!(quantity > 0)) problem = 'lineQuantityInvalid';
      else if (!(unitPrice >= 0)) problem = 'linePriceInvalid';
      else if (quantity < parseFloat(line.quantity_converted || 0)) problem = 'lineBelowConverted';
      if (problem) {
        setActiveTab('lines');
        showError(t('quotations.view.messages.validationError'),
          t(`quotations.view.messages.${problem}`, { line: i + 1, quantity: parseFloat(line.quantity_converted || 0) }));
        return null;
      }
      payload.push({
        // Lines loaded from the server keep their id; new ones (temp_id) are inserted
        line_id: line.line_id && !line.temp_id ? line.line_id : null,
        product_id: parseInt(line.product_id),
        line_number: i + 1,
        description: line.description || '',
        quantity,
        unit_price: unitPrice,
        discount_percentage: parseFloat(line.discount_percentage || 0),
        tax_percentage: parseFloat(line.tax_percentage || 0),
        lead_time_days: parseInt(line.lead_time_days || 0),
        notes: line.notes || ''
      });
    }
    return payload;
  };

  const handleSave = async () => {
    // Validate required fields
    if (!editData.customer_id) {
      showError(t('quotations.view.messages.validationError'), t('quotations.view.messages.customerRequired'));
      return;
    }

    const linesPayload = linesLoaded ? buildLinesPayload() : undefined;
    if (linesPayload === null) return;

    const failedTitle = createMode ? t('quotations.view.messages.failedToCreateQuote') : t('quotations.view.messages.failedToUpdateQuote');

    try {
      const url = createMode
        ? API_CONFIG.ENDPOINTS.QUOTATIONS
        : getApiUrl(`/api/quotes/${quoteId}`);

      const method = createMode ? 'POST' : 'PUT';

      // Header and lines go together: the server saves all of it or nothing, removes the lines
      // that are no longer here and computes the totals from the saved lines
      const { lines: _ignored, ...header } = editData;
      const dataToSend = {
        ...header,
        customer_id: editData.customer_id ? parseInt(editData.customer_id) : null,
        contact_id: editData.contact_id ? parseInt(editData.contact_id) : null,
        ...(linesPayload !== undefined && { lines: linesPayload })
      };

      const response = await apiFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(dataToSend)
      });

      if (!response.ok) {
        const parsed = await showApiError(response, showError, tCommon, failedTitle);
        setSaveError(getErrorMessage(parsed, tCommon));
        return;
      }

      const result = await response.json();
      setHasChanges(false);
      setSaveError(null);

      showSuccess(
        createMode ? t('quotations.view.messages.quoteCreated') : t('quotations.view.messages.quoteUpdated'),
        t(createMode ? 'quotations.view.messages.quoteCreatedSuccess' : 'quotations.view.messages.quoteUpdatedSuccess', {
          quotationNumber: editData.quotation_number
        })
      );

      // Show what was stored (server totals, line ids)
      if (createMode && result.id) {
        const quoteResponse = await apiFetch(getApiUrl(`/api/quotes/${result.id}`));
        if (quoteResponse.ok) {
          const savedData = await quoteResponse.json();
          setQuote(savedData);
          setEditData(savedData);
          if (onSave) onSave(savedData);
        }
        await fetchLines(result.id);
      } else if (!createMode) {
        await fetchQuote();
        if (onSave) onSave(editData);
      }
    } catch (error) {
      console.error('Error saving quote:', error);
      // Network error: keep the form (and what was typed) and show the problem above it
      const errorMessage = t('quotations.view.messages.savingError');
      setSaveError(errorMessage);
      showError(failedTitle, errorMessage);
    }
  };

  const handleCancel = () => {
    setSaveError(null);
    if (createMode) {
      if (onCancel) onCancel();
    } else {
      setEditData(quote);
      setHasChanges(false);
      if (onCancel) onCancel();
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(t('quotations.view.messages.deleteQuoteConfirmation'))) {
      return;
    }

    try {
      const response = await apiFetch(getApiUrl(`/api/quotes/${quoteId}`), {
        method: 'DELETE'
      });

      if (response.ok) {
        if (onBack) onBack();
      } else {
        // e.g. 422 when it was already converted: the API says why; the quotation stays on screen
        const parsed = await showApiError(response, showError, tCommon, t('quotations.view.messages.failedToDeleteQuote'));
        setSaveError(getErrorMessage(parsed, tCommon));
      }
    } catch (error) {
      console.error('Error deleting quote:', error);
      const errorMessage = t('quotations.view.messages.deletingError');
      setSaveError(errorMessage);
      showError(t('quotations.view.messages.failedToDeleteQuote'), errorMessage);
    }
  };

  const updateQuoteTotals = useCallback((updatedLines) => {
    let subtotalBeforeTax = 0;
    let totalTaxAmount = 0;
    let discountAmount = 0;

    updatedLines.forEach(line => {
      const lineGross = (line.quantity || 0) * (line.unit_price || 0);
      const lineDiscount = lineGross * ((line.discount_percentage || 0) / 100);
      const lineAfterDiscount = lineGross - lineDiscount;
      const lineTax = lineAfterDiscount * ((line.tax_percentage || 0) / 100);

      subtotalBeforeTax += lineAfterDiscount;
      totalTaxAmount += lineTax;
      discountAmount += lineDiscount;
    });

    const finalTotal = subtotalBeforeTax + totalTaxAmount;

    setEditData(prev => ({
      ...prev,
      subtotal: subtotalBeforeTax,
      discount_amount: discountAmount,
      tax_amount: totalTaxAmount,
      total_amount: finalTotal
    }));
  }, []);

  const handleLinesChange = useCallback((updatedLines) => {
    setLines(updatedLines);
    setHasChanges(true);
    updateQuoteTotals(updatedLines);
  }, [updateQuoteTotals]);

  const getStatusText = (status) => (
    STATUS_KEYS[status] ? t(`quotations.view.status.${STATUS_KEYS[status]}`) : (status || t('quotations.view.status.draft'))
  );

  const hasConversions = lines.some(line => parseFloat(line.quantity_converted || 0) > 0);
  const isConverted = !createMode && quote?.status === 'CONVERTED';
  // A converted quotation only changes its notes and terms (the server refuses the rest)
  const canEditHeader = createMode || (editMode && !isConverted);
  const canEditNotes = createMode || editMode;
  const canDelete = !hasConversions && !['CONVERTED', 'PARTIALLY_CONVERTED'].includes(quote?.status);
  const statusOptions = createMode
    ? ['DRAFT', ...QUOTE_STATUS_TRANSITIONS.DRAFT]
    : quoteStatusOptions(quote?.status || 'DRAFT', hasConversions);
  // The quotation's customer stays in the select even if it is no longer active
  const customerOptions = editData.customer_id && !customers.some(c => c.customer_id === parseInt(editData.customer_id))
    ? [...customers, { customer_id: parseInt(editData.customer_id), customer_name: editData.customer_name || quote?.customer_name }]
    : customers;

  const renderGeneralTab = () => (
    <div className="tab-content">
      <div className="form-section">
        <h3>{t('quotations.view.sections.quotationInformation')}</h3>
        <div className="form-grid">
          <div className="form-group">
            <label>{t('quotations.view.fields.quotationNumber')} *</label>
            <input
              type="text"
              value={editData.quotation_number || ''}
              onChange={(e) => handleFieldChange('quotation_number', e.target.value)}
              disabled={!createMode}
              required
            />
          </div>
          <div className="form-group">
            <label>{t('quotations.view.fields.customer')} *</label>
            <select
              value={editData.customer_id || ''}
              onChange={(e) => handleFieldChange('customer_id', e.target.value)}
              disabled={!canEditHeader || (hasConversions && !createMode)}
              required
            >
              <option value="" disabled hidden>{t('quotations.view.placeholders.selectCustomer')}</option>
              {customerOptions.map(customer => (
                <option key={customer.customer_id} value={customer.customer_id}>
                  {customer.customer_name}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>{t('quotations.view.formLabels.quoteDate')} *</label>
            {canEditHeader ? (
              <input
                type="date"
                value={formatDateForInput(editData.quotation_date)}
                onChange={(e) => handleFieldChange('quotation_date', e.target.value)}
                required
              />
            ) : (
              <span className="form-value">
                {editData.quotation_date ? formatAppDate(editData.quotation_date) : t('quotations.view.formValues.notSet')}
              </span>
            )}
          </div>
          <div className="form-group">
            <label>{t('quotations.view.formLabels.validUntil')} *</label>
            {canEditHeader ? (
              <input
                type="date"
                value={formatDateForInput(editData.valid_until_date)}
                onChange={(e) => handleFieldChange('valid_until_date', e.target.value)}
                required
              />
            ) : (
              <span className="form-value">
                {editData.valid_until_date ? formatAppDate(editData.valid_until_date) : t('quotations.view.formValues.notSet')}
              </span>
            )}
          </div>
          <div className="form-group">
            <label>{t('quotations.view.formLabels.status')}</label>
            <select
              value={editData.status || 'DRAFT'}
              onChange={(e) => handleFieldChange('status', e.target.value)}
              disabled={!canEditHeader || statusOptions.length < 2}
            >
              {statusOptions.map(status => (
                <option key={status} value={status}>{t(`quotations.view.statusOptions.${STATUS_KEYS[status]}`)}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>{t('quotations.view.formLabels.currency')}</label>
            <select
              value={editData.currency || 'USD'}
              onChange={(e) => handleFieldChange('currency', e.target.value)}
              disabled={!canEditHeader}
            >
              <option value="USD">{t('common.currencies.usd')}</option>
              <option value="EUR">{t('common.currencies.eur')}</option>
              <option value="GBP">{t('common.currencies.gbp')}</option>
              <option value="SEK">{t('common.currencies.sek')}</option>
            </select>
          </div>
        </div>
      </div>

      <div className="form-section">
        <h3>{t('quotations.view.formLabels.terms')}</h3>
        <div className="form-grid">
          <div className="form-group">
            <label>{t('quotations.view.formLabels.paymentTerms')}</label>
            <select
              value={editData.payment_terms || 'NET_30'}
              onChange={(e) => handleFieldChange('payment_terms', e.target.value)}
              disabled={!canEditHeader}
            >
              <option value="UPFRONT">{t('quotations.view.paymentTermsOptions.upfront')}</option>
              <option value="NET_30">{t('quotations.view.paymentTermsOptions.net30')}</option>
              <option value="NET_45">{t('quotations.view.paymentTermsOptions.net45')}</option>
              <option value="NET_60">{t('quotations.view.paymentTermsOptions.net60')}</option>
              <option value="NET_90">{t('quotations.view.paymentTermsOptions.net90')}</option>
              <option value="NET_120">{t('quotations.view.paymentTermsOptions.net120')}</option>
            </select>
          </div>
          <div className="form-group">
            <label>{t('quotations.view.formLabels.deliveryTerms')}</label>
            <input
              type="text"
              value={editData.delivery_terms || ''}
              onChange={(e) => handleFieldChange('delivery_terms', e.target.value)}
              disabled={!canEditHeader}
            />
          </div>
        </div>
      </div>
    </div>
  );

  const renderLinesTab = () => (
    <div className="tab-content">
      <EditableQuoteLines
        data={lines}
        parentEntity={quote || { quote_id: quoteId }}
        editMode={canEditHeader}
        onLinesChange={handleLinesChange}
      />
    </div>
  );

  const renderContactsTab = () => (
    <div className="tab-content">
      <div className="form-section">
        <h3>{t('quotations.view.formLabels.quoteContacts')}</h3>

        {!editData.customer_id ? (
          <div className="no-data">{t('quotations.view.contactsMessages.selectCustomerFirst')}</div>
        ) : contacts.length === 0 ? (
          <div className="no-data">{t('quotations.view.contactsMessages.noContactsFound')}</div>
        ) : (
          <div className="contacts-list">
            <div className="form-group">
              <label>{t('quotations.view.formLabels.primaryContact')}</label>
              <select
                value={editData.contact_id || ''}
                onChange={(e) => handleFieldChange('contact_id', e.target.value ? parseInt(e.target.value) : null)}
                disabled={!canEditHeader}
              >
                {/* No contact of its own: the customer's primary contact */}
                <option value="">{t('quotations.view.formValues.customerPrimaryContact')}</option>
                {contacts.map(contact => (
                  <option key={contact.contact_id} value={contact.contact_id}>
                    {contact.first_name} {contact.last_name} {contact.position ? `(${contact.position})` : ''} - {contact.email}
                  </option>
                ))}
              </select>
            </div>

            <div className="contacts-table">
              <h4>{t('quotations.view.formLabels.availableContacts')}</h4>
              <table>
                <thead>
                  <tr>
                    <th>{t('quotations.view.tableHeaders.name')}</th>
                    <th>{t('quotations.view.tableHeaders.position')}</th>
                    <th>{t('quotations.view.tableHeaders.department')}</th>
                    <th>{t('quotations.view.tableHeaders.email')}</th>
                    <th>{t('quotations.view.tableHeaders.phone')}</th>
                    <th>{t('quotations.view.tableHeaders.primary')}</th>
                  </tr>
                </thead>
                <tbody>
                  {contacts.map(contact => (
                    <tr key={contact.contact_id} className={contact.contact_id === parseInt(editData.contact_id) ? 'selected' : ''}>
                      <td>{contact.first_name} {contact.last_name}</td>
                      <td>{contact.position || '-'}</td>
                      <td>{contact.department || '-'}</td>
                      <td>{contact.email || '-'}</td>
                      <td>{contact.phone || '-'}</td>
                      <td>{contact.is_primary ? '✓' : ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderNotesTab = () => (
    <div className="tab-content">
      <div className="form-section">
        <h3>{t('quotations.view.formLabels.notesAndTerms')}</h3>
        <div className="form-grid">
          <div className="form-group full-width">
            <label>{t('quotations.view.formLabels.internalNotes')}</label>
            <textarea
              value={editData.notes || ''}
              onChange={(e) => handleFieldChange('notes', e.target.value)}
              disabled={!canEditNotes}
              rows="4"
            />
          </div>
          <div className="form-group full-width">
            <label>{t('quotations.view.formLabels.termsAndConditions')}</label>
            <textarea
              value={editData.terms_conditions || ''}
              onChange={(e) => handleFieldChange('terms_conditions', e.target.value)}
              disabled={!canEditNotes}
              rows="6"
            />
          </div>
        </div>
      </div>
    </div>
  );

  const fetchLinkedPOs = async () => {
    if (!quoteId || linkedPOs !== null) return;
    try {
      const response = await apiFetch(getApiUrl(`/api/production/orders?quotation_id=${quoteId}&page_size=50`));
      setLinkedPOs(response.ok ? (await response.json()).orders || [] : []);
    } catch { setLinkedPOs([]); }
  };

  const renderProductionOrdersTab = () => (
    <div className="tab-content">
      <div className="form-section">
        <h3>{t('linkedProductionOrders.title')}</h3>
        {linkedPOs === null ? (
          <div className="loading">{t('linkedProductionOrders.loading')}</div>
        ) : linkedPOs.length === 0 ? (
          <div className="no-data" style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>{t('linkedProductionOrders.noneForQuote')}</div>
        ) : (
          <table className="data-table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th>{t('production:fields.poNumber')}</th>
                <th>{t('common:ui.status')}</th>
                <th>{t('common:ui.priority')}</th>
                <th>{t('invoices.view.fields.dueDate')}</th>
              </tr>
            </thead>
            <tbody>
              {linkedPOs.map(po => (
                <tr key={po.production_order_id}>
                  <td>
                    <EntityLink
                      type="production-order"
                      id={po.po_number}
                      label={po.po_number}
                      returnLabel={quote?.quotation_number}
                      returnType="quote"
                      returnId={quoteId}
                    />
                  </td>
                  <td><span className={`status-badge status-${po.status?.toLowerCase()}`}>{po.status}</span></td>
                  <td>{po.priority}</td>
                  <td>{po.planned_due_date ? formatAppDate(po.planned_due_date) : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );

  if (loading) {
    return <div className="view-container"><div className="loading">{t('quotations.view.loading')}</div></div>;
  }

  if (error) {
    return <div className="view-container"><div className="error-message">{error}</div></div>;
  }

  if (!quote && !createMode) {
    return <div className="view-container"><div className="no-data">{t('quotations.view.titles.noQuotationFound')}</div></div>;
  }

  return (
    <div className="view-container">
      {saveError && <div className="error-message" role="alert">{saveError}</div>}
      {editMode && isConverted && (
        <div className="info-message" style={{ padding: '10px 15px', background: '#fff3cd', border: '1px solid #ffc107', borderRadius: '4px', margin: '0 0 15px 0', color: '#856404' }}>
          {t('quotations.view.messages.convertedEditHint')}
        </div>
      )}
      <div className="view-header">
        <div className="header-title">
          <BackButton onBack={onBack} entityType="quote" entityIds={[quoteId, quote?.quotation_id]} />
          <h1>
            {createMode ? t('quotations.view.titles.newQuotation') : editData.quotation_number || t('quotations.view.titles.quotation')}
          </h1>
          {!createMode && (
            <span className={`status-badge ${
              editData.status === 'ACCEPTED' ? 'status-completed' :
              editData.status === 'PENDING' ? 'status-active' :
              editData.status === 'REJECTED' ? 'status-cancelled' :
              editData.status === 'EXPIRED' ? 'status-inactive' :
              editData.status === 'CONVERTED' ? 'status-success' :
              editData.status === 'PARTIALLY_CONVERTED' ? 'status-warning' :
              'status-pending'
            }`}>
              {getStatusText(editData.status || 'DRAFT')}
            </span>
          )}
        </div>
        <div className="header-actions">
          {!createMode && !editMode && (
            <>
              {(editData.status === 'ACCEPTED' || editData.status === 'PARTIALLY_CONVERTED') && (
                <button className="btn-action" onClick={() => {
                  setShowEnhancedConversionModal(true);
                }}>
                  {editData.status === 'PARTIALLY_CONVERTED' ? t('quotations.view.buttons.convertRemaining') : t('quotations.view.buttons.convert')}
                </button>
              )}
              <button
                className="btn-secondary"
                onClick={onEdit}
                title={isConverted ? t('quotations.view.messages.convertedEditHint') : undefined}
              >
                {t('quotations.view.buttons.edit')}
              </button>
              {canDelete && (
                <button className="btn-danger" onClick={handleDelete}>{t('quotations.toolbar.buttons.delete')}</button>
              )}
            </>
          )}
          {(createMode || editMode) && (
            <>
              <button className="btn-secondary" onClick={handleCancel}>{t('quotations.view.buttons.cancel')}</button>
              <button
                className="btn-primary"
                onClick={handleSave}
                disabled={(!hasChanges && !createMode) || !editData.customer_id}
                title={!editData.customer_id ? t('quotations.view.placeholders.selectCustomer') : ''}
              >
                {createMode ? t('quotations.view.buttons.create') : t('quotations.view.buttons.save')}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="tabs">
        <div className="tab-list">
          <button
            className={`tab-item ${activeTab === 'general' ? 'active' : ''}`}
            onClick={() => setActiveTab('general')}
          >
            {t('quotations.view.tabs.general')}
          </button>
          <button
            className={`tab-item ${activeTab === 'lines' ? 'active' : ''}`}
            onClick={() => setActiveTab('lines')}
          >
            {t('quotations.view.tabs.lines')}
          </button>
          <button
            className={`tab-item ${activeTab === 'contacts' ? 'active' : ''}`}
            onClick={() => setActiveTab('contacts')}
          >
            {t('quotations.view.tabs.contacts')}
          </button>
          <button
            className={`tab-item ${activeTab === 'notes' ? 'active' : ''}`}
            onClick={() => setActiveTab('notes')}
          >
            {t('quotations.view.tabs.notes')}
          </button>
          {!createMode && (
            <button
              className={`tab-item ${activeTab === 'production-orders' ? 'active' : ''}`}
              onClick={() => { setActiveTab('production-orders'); fetchLinkedPOs(); }}
            >
              {t('linkedProductionOrders.title')}
            </button>
          )}
        </div>

        <div className="tab-panels">
          {activeTab === 'general' && renderGeneralTab()}
          {activeTab === 'lines' && renderLinesTab()}
          {activeTab === 'contacts' && renderContactsTab()}
          {activeTab === 'notes' && renderNotesTab()}
          {activeTab === 'production-orders' && renderProductionOrdersTab()}
        </div>
      </div>

      {showConvertModal && (
        <ConvertToProductionModal
          quote={editData}
          lines={lines}
          onClose={() => setShowConvertModal(false)}
          onSuccess={(newOrder) => {
            setShowConvertModal(false);
            // Navigate to production page
            navigate('/production');
          }}
        />
      )}

      {showEnhancedConversionModal && (
        <EnhancedQuoteConversion
          quotation={editData}
          onClose={() => setShowEnhancedConversionModal(false)}
          onConversionComplete={(salesOrder) => {
            setShowEnhancedConversionModal(false);
            // With production orders the conversion shows its own message (salesOrder is null)
            if (salesOrder) {
              showSuccess(
                t('quotations.view.messages.quoteConvertedSuccess'),
                t('quotations.view.messages.salesOrderCreatedSuccess', { orderNumber: salesOrder.so_number })
              );
            }
            // Refresh the quote data to reflect the updated status
            fetchQuote();
            // Stay on the current quote view - don't navigate away
          }}
        />
      )}

    </div>
  );
});

ViewQuote.displayName = 'ViewQuote';

export default ViewQuote;